import {
	MAX_CURRENT_ANSWERS,
	MAX_CURRENT_ANSWERS_BYTES,
	MAX_CURRENT_ANSWER_LENGTH,
} from "@performyard/service-contracts";
import type { FormAuthoringCoachCurrentAnswer } from "@performyard/service-contracts";

/**
 * The legacy review page's own answer collector (`review.js`), reached through the global it assigns
 * itself. Reusing it rather than scraping the DOM here means the coach sees exactly what Save would
 * post — one collector, no second implementation to drift.
 */
type ReviewFormGlobal = {
	collect?: (cleanInvalidAnswers?: boolean) => { answers?: unknown[] } | undefined;
};

/**
 * The question positions `collect()` reports, in its own order.
 *
 * `collect()` walks the rendered `.question-block`s and keys its array by iteration order, so a
 * hidden calculator question — skipped in the markup as a `.question-skip` — costs a slot and shifts
 * everything after it. The stored `answers` array keeps that slot (the save handler re-inserts a
 * `None` for it), and so does the coach, which addresses answers the same way the page's `data-index`
 * does. So the array position is translated back through `data-index` rather than sent as-is; without
 * it, a form with a hidden calculator anywhere but last would land each unsaved answer on the wrong
 * question. Same selector as `collect()`, so the two lists line up element for element.
 */
const questionIndexes = (): number[] =>
	Array.from(document.querySelectorAll<HTMLElement>("#report .question-block")).map((block) =>
		Number(block.dataset.index)
	);

/**
 * The author's answers as they currently stand on screen, including unsaved edits.
 *
 * Values richer than text/number/list (goal discussions) are dropped: they can't be expressed in the
 * request contract, and the agent falls back to the saved copy for those positions.
 */
export const collectReviewAnswers = (): FormAuthoringCoachCurrentAnswer[] => {
	if (typeof window === "undefined") return [];

	const reviewForm = (window as unknown as { _pyForm?: ReviewFormGlobal })._pyForm;
	if (typeof reviewForm?.collect !== "function") return [];

	let answers: unknown[] | undefined;
	try {
		// `false` = don't clean invalid answers: the cleaning path rewrites numeric inputs and steals
		// focus, which must never happen just because someone asked for feedback.
		answers = reviewForm.collect(false)?.answers;
	} catch {
		// A collector failure must not take the whole request down — the agent still has the saved copy.
		return [];
	}

	if (!Array.isArray(answers)) return [];

	const indexes = questionIndexes();

	const collected: FormAuthoringCoachCurrentAnswer[] = [];
	let spent = 0;

	for (const [position, value] of answers.slice(0, MAX_CURRENT_ANSWERS).entries()) {
		const index = indexes[position];
		// A position with no block behind it, or one past the contract's cap, is dropped rather than
		// guessed at: the saved copy for that question is the safer thing for the coach to read.
		if (!Number.isInteger(index) || index < 0 || index > MAX_CURRENT_ANSWERS) continue;

		const answer = toAnswer(index, value);
		if (!answer) continue;

		// Stop at the budget rather than send a body the route will reject unread: the questions that
		// did fit are still coachable, and the rest fall back to the saved copy.
		spent += serializedSize(answer);
		if (spent > MAX_CURRENT_ANSWERS_BYTES) break;

		collected.push(answer);
	}

	return collected;
};

/** One collected value in the shape the contract carries, or null when it can't be expressed. */
const toAnswer = (index: number, value: unknown): FormAuthoringCoachCurrentAnswer | null => {
	if (typeof value === "string") return { index, value: value.slice(0, MAX_CURRENT_ANSWER_LENGTH) };
	if (typeof value === "number" && Number.isFinite(value)) return { index, value };
	if (Array.isArray(value) && value.every((entry) => typeof entry === "string")) {
		return {
			index,
			value: (value as string[]).slice(0, 100).map((entry) => entry.slice(0, MAX_CURRENT_ANSWER_LENGTH)),
		};
	}
	return null;
};

/** Bytes this answer will occupy in the request body. Encoded, not measured in characters: the budget
 *  is the route's byte limit, and a review written in Japanese runs about three bytes per character. */
const serializedSize = (answer: FormAuthoringCoachCurrentAnswer): number => {
	const json = JSON.stringify(answer);
	return typeof TextEncoder === "undefined" ? json.length : new TextEncoder().encode(json).length;
};
